import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { LOGISTIQUE_SECTIONS } from "@/lib/ficheSpectacle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save } from "lucide-react";

const Pick = ({ value, options, onChange, testid }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger className="h-8 text-xs" data-testid={testid}><SelectValue placeholder="—" /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
  </Select>
);

const TableBlock = ({ block, value = [], onChange, tid }) => {
  const setCell = (i, k, v) => { const next = block.rows.map((_, j) => ({ ...(value[j] || {}) })); next[i][k] = v; onChange(next); };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="text-muted-foreground text-left">
          <th className="py-1 pr-2 font-medium">{block.first}</th>
          {block.cols.map((c) => <th key={c.key} className="py-1 px-1 font-medium">{c.label}</th>)}
        </tr></thead>
        <tbody>{block.rows.map((r, i) => {
          const row = value[i] || {};
          return (
            <tr key={i} className="border-t border-border/60">
              <td className="py-1 pr-2 min-w-[9rem]">{r.free
                ? <Input className="h-8 text-xs" placeholder={r.placeholder || "—"} value={row.label || ""} onChange={(e) => setCell(i, "label", e.target.value)} data-testid={`${tid}-${i}-label`} />
                : <span className="font-medium">{r.label}</span>}</td>
              {block.cols.map((c) => (
                <td key={c.key} className="py-1 px-1 min-w-[6.5rem]">
                  {c.type === "select" ? <Pick value={row[c.key]} options={c.options} onChange={(v) => setCell(i, c.key, v)} testid={`${tid}-${i}-${c.key}`} />
                    : c.type === "computed" ? <span className="px-2 font-semibold" data-testid={`${tid}-${i}-${c.key}`}>{c.compute(row)}</span>
                    : <Input className="h-8 text-xs" type={c.type === "number" ? "number" : "text"} value={row[c.key] || ""} onChange={(e) => setCell(i, c.key, e.target.value)} data-testid={`${tid}-${i}-${c.key}`} />}
                </td>
              ))}
            </tr>
          );
        })}</tbody>
      </table>
    </div>
  );
};

const Block = ({ block, value, onChange, tid }) => {
  if (block.type === "table") return (
    <div>{block.title && <div className="text-xs uppercase tracking-wider text-primary/80 mb-1">{block.title}</div>}
      <TableBlock block={block} value={value} onChange={onChange} tid={tid} /></div>
  );
  if (block.type === "text") return (
    <div><div className="text-xs uppercase tracking-wider text-primary/80 mb-1">{block.label}</div>
      <Textarea rows={2} className="text-xs" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} /></div>
  );
  const v = value || {};
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[10rem_10rem_1fr] gap-2 items-center">
      <span className="text-xs font-medium">{block.label}</span>
      <Pick value={v.etat} options={block.options} onChange={(e) => onChange({ ...v, etat: e })} testid={`${tid}-etat`} />
      {block.note ? <Input className="h-8 text-xs" placeholder="Infos" value={v.note || ""} onChange={(e) => onChange({ ...v, note: e.target.value })} data-testid={`${tid}-note`} /> : <span />}
    </div>
  );
};

export const LogistiqueSections = ({ concertId, initial }) => {
  const [data, setData] = useState(initial || {});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!dirty) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);
  const set = (s, b, v) => { setDirty(true); setData((d) => ({ ...d, [s]: { ...(d[s] || {}), [b]: v } })); };
  const save = async () => {
    setSaving(true);
    try { await api.put(`/concerts/${concertId}/fiche-spectacle`, { logistique: data }); setDirty(false); toast.success("Rubriques enregistrées"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4" data-testid="logistique-sections">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold">Logistique & annexes</h2>
        <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="save-logistique-button">
          <Save className="h-4 w-4 mr-2" />{saving ? "Enregistrement..." : dirty ? "Enregistrer *" : "Enregistrer"}
        </Button>
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        {LOGISTIQUE_SECTIONS.map((s) => (
          <section key={s.key} className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid={`logistique-${s.key}`}>
            <h3 className="font-display font-bold text-primary">{s.title}</h3>
            {s.blocks.map((b) => (
              <Block key={b.key} block={b} value={data[s.key]?.[b.key]} onChange={(v) => set(s.key, b.key, v)} tid={`logi-${s.key}-${b.key}`} />
            ))}
          </section>
        ))}
      </div>
    </div>
  );
};
